/**
 * Undo/redo state for the manuscript editor, as a pure reducer over content snapshots.
 *
 * The editor is a contentEditable whose innerHTML gets reassigned by search highlighting,
 * find & replace, and AI insertion. Those assignments destroy the browser's native undo
 * stack, so history is tracked here as plain strings instead of DOM state.
 */

export type EditKind = 'typing' | 'discrete' | 'amend'

export interface HistoryEntry {
  /** Editor innerHTML at this point in time. */
  content: string
  /** Caret position as a text-node character offset, or -1 when unknown. */
  caretOffset: number
}

export interface HistoryState {
  past: HistoryEntry[]
  present: HistoryEntry
  future: HistoryEntry[]
  lastKind: EditKind
  lastEditAt: number
  coalesceStartedAt: number
}

/** Maximum gap between keystrokes that still counts as the same typing burst. */
export const TYPING_COALESCE_WINDOW_MS = 600
/** Cap on a single burst, so one uninterrupted paragraph is not one undo step. */
export const MAX_COALESCE_SPAN_MS = 5000
export const MAX_HISTORY_DEPTH = 200

export function createHistory(entry: HistoryEntry): HistoryState {
  return {
    past: [],
    present: entry,
    future: [],
    lastKind: 'discrete',
    lastEditAt: 0,
    coalesceStartedAt: 0
  }
}

const pushPast = (past: HistoryEntry[], entry: HistoryEntry): HistoryEntry[] => {
  const next = [...past, entry]
  return next.length > MAX_HISTORY_DEPTH ? next.slice(next.length - MAX_HISTORY_DEPTH) : next
}

export function recordEdit(
  state: HistoryState,
  entry: HistoryEntry,
  kind: EditKind,
  now: number
): HistoryState {
  // App state flows back down into the editor on every render; without this guard the
  // round trip would record an edit for content that never actually changed.
  if (entry.content === state.present.content) return state

  // 'amend' exists for the AI-insert highlight unwrap, which fires 4s after the insert.
  // Treating it as its own step would make the first Cmd+Z re-wrap the highlight div
  // rather than remove the inserted prose.
  if (kind === 'amend') {
    return { ...state, present: entry, lastEditAt: now }
  }

  const canCoalesce =
    kind === 'typing' &&
    state.lastKind === 'typing' &&
    now - state.lastEditAt <= TYPING_COALESCE_WINDOW_MS &&
    now - state.coalesceStartedAt <= MAX_COALESCE_SPAN_MS

  if (canCoalesce) {
    return { ...state, present: entry, future: [], lastEditAt: now }
  }

  return {
    past: pushPast(state.past, state.present),
    present: entry,
    future: [],
    lastKind: kind,
    lastEditAt: now,
    coalesceStartedAt: now
  }
}

// Both navigations reset the burst markers, so typing straight after a Cmd+Z starts a
// fresh step instead of merging into the state we just travelled back to.
export function undo(state: HistoryState): HistoryState {
  if (state.past.length === 0) return state
  return {
    past: state.past.slice(0, -1),
    present: state.past[state.past.length - 1],
    future: [state.present, ...state.future],
    lastKind: 'discrete',
    lastEditAt: 0,
    coalesceStartedAt: 0
  }
}

export function redo(state: HistoryState): HistoryState {
  if (state.future.length === 0) return state
  const [next, ...rest] = state.future
  return {
    past: pushPast(state.past, state.present),
    present: next,
    future: rest,
    lastKind: 'discrete',
    lastEditAt: 0,
    coalesceStartedAt: 0
  }
}

export const canUndo = (state: HistoryState): boolean => state.past.length > 0
export const canRedo = (state: HistoryState): boolean => state.future.length > 0
